import {
  convertEventSelectToTarget,
  DataTable,
  formatDataToLabelValue,
  ModalConfirmAction,
  PageWrapper,
  PROFILE_URL_PATH,
  STATUSPAGE,
} from '@repo/ui';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { GeneralFilters } from 'presentation/app/components/general-filters';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from 'reactstrap';

import { makeColumnsProfessionalsLists } from '../../../common/columns/columns-professionals-lists';
import {
  initialValues,
  validationSchema,
} from '../../../common/validation/lists-filters-validation';
import { useProfessionalsLists } from '../../../hooks/useProfessionalsLists';

export const TeachersList = () => {
  // Hooks
  const { getLegends } = useLegends();
  const {
    isUnlinkModalOpen,
    selectedProfessionalId,
    unlinkModalButtonsStatusPage,
    listStatusPage,
    professionalsData,
    paginationMetadata,
    groupsUser,
    internshipLocationsSelectOptions,
    debouncedRequestGetAllProfessionals,
    toggleUnlinkModal,
    requestGetInternshipLocationsSelectOptions,
    requestUnlinkProfessional,
    handlePageChange,
    handleSortRemote,
    setListStatusPage,
    handleSearchRemote,
    removeProfessionalsData,
    handleRowsPerPageChange,
  } = useProfessionalsLists();

  const validation = useFormik({
    initialValues,
    validationSchema,

    // There is no onSubmit function on this form (no submit button).
    // The API request is sent everytime any filter changes, so it is controlled via useEffect and DataTable's remote pagination.
    onSubmit: () => {
      return;
    },
  });

  // useEffect responsible for calling API and fetching DataTable's data
  useEffect(() => {
    const valuesToSubmit = {
      entity: validation.values?.entity?.value,
      course: validation.values?.course?.value,
      semester: validation.values?.semester?.value,
      unit: validation.values?.unit?.value,
      group: validation.values?.group?.value,
      page: paginationMetadata.selectedPage.toString(),
      length: paginationMetadata.rowsPerPage.toString(),
      location: validation.values?.local?.value,
      order: paginationMetadata.sortDirection,
      orderColumn: paginationMetadata.orderColumn,
      query: paginationMetadata.searchString,
      profileType: PROFILE_TYPE.TEACHER,
    };
    setListStatusPage(STATUSPAGE.LOADING);

    debouncedRequestGetAllProfessionals(valuesToSubmit);

    return () => {
      removeProfessionalsData();
    };
  }, [
    validation.values?.entity,
    validation.values?.course,
    validation.values?.unit,
    validation.values?.semester,
    validation.values?.group,
    validation.values?.local,
    paginationMetadata,
  ]);

  const populateDependentInputs = useCallback(() => {
    const selectedOrganization = validation.values.entity?.value;
    const selectedCourse = validation.values.course?.value;

    const selectedSemester = validation.values.semester?.value || '';
    const selectedCurricularUnit = validation.values.unit?.value || '';

    requestGetInternshipLocationsSelectOptions({
      courseHashIds: selectedCourse,
      semester: selectedSemester,
      organizationHashIds: selectedOrganization,
      curricularUnit: selectedCurricularUnit,
      groups: validation.values.group?.value,
    });
  }, []);

  const navigate = useNavigate();

  // Constants
  const noDataConfig = {
    noDataTitle: 'Nenhum professor encontrado',
    noDataDescription:
      'Use os filtros acima para pesquisar professores por nome, grupo ou local de estágio.',
  };

  return (
    <PageWrapper title={getLegends('PROFESSOR', true)}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>{getLegends('PROFESSOR', true)}</h4>

        {/* {currentUser?.activeProfile?.profileId !== PERSON_TYPE.DIRECTOR && (
          <button
            onClick={() => {
              navigate(`/pre-cadastro/${PROFILE_URL_PATH.TEACHERS}`);
            }}
            type="submit"
            className="btn btn-primary waves-effect waves-light d-flex align-items-center gap-1"
          >
            <i className="ri-add-line" style={{ fontSize: '1rem' }}></i>
            Novo {getLegends('PROFESSOR')}
          </button>
        )} */}
      </div>
      <Card>
        <GeneralFilters
          validation={validation}
          additionalDependentInputs={[
            {
              id: 'local',
              label: `${getLegends('LOCAL_ESTAGIO', false)}:`,
              onChange: (e) =>
                validation.handleChange(convertEventSelectToTarget('local', e)),
              options: formatDataToLabelValue(
                internshipLocationsSelectOptions as never,
                'id',
                'nome',
              ),
              placeholder: `Todos os ${getLegends('LOCAL_ESTAGIO', true)}`,
              isVisible: true,
              isSelect: true,
            },
            {
              id: 'group',
              label: `${getLegends('GRUPO', false)}:`,
              onChange: (e) =>
                validation.handleChange(convertEventSelectToTarget('group', e)),
              options: formatDataToLabelValue(
                groupsUser as never,
                'id',
                'nome',
              ),
              placeholder: `Todos os ${getLegends('GRUPO', true)}`,
              isVisible: true,
              isSelect: true,
              isMulti: false,
            },
          ]}
          populateAdditionalDependentInputs={populateDependentInputs}
        />

        <CardBody>
          <DataTable
            noDataTitle={noDataConfig.noDataTitle}
            noDataDescription={noDataConfig.noDataDescription}
            actions={[
              {
                id: 1,
                name: 'overview',
                label: 'Visão Geral',
                onPress: (cellProps) => {
                  navigate({
                    pathname: `/gerenciamento-de-profissional/${PROFILE_URL_PATH.TEACHERS}/${cellProps.id}`,
                    search: '?painel=visao-geral',
                  });
                },
                icon: 'ri-line-chart-line',
                classNameColorIcon: 'primary',
              },
              {
                id: 2,
                name: 'view',
                label: 'Visualizar',
                onPress: (cellProps) => {
                  navigate({
                    pathname: `/gerenciamento-de-profissional/${PROFILE_URL_PATH.TEACHERS}/${cellProps.id}`,
                    search: '?painel=dados-cadastrais',
                  });
                },
                icon: 'ri-file-search-line',
                classNameColorIcon: 'primary',
              },
              // {
              //   id: 2,
              //   name: 'edit',
              //   label: 'Editar',
              //   onPress: (cellProps) => {
              //     navigate(
              //       `/gerenciamento-de-profissional/${PROFILE_URL_PATH.TEACHERS}/editar/${cellProps.id}`, - to change in privateRoutes later
              //     );
              //   },
              //   icon: 'ri-edit-line',
              //   classNameColorIcon: 'primary',
              // },
              // {
              //   id: 3,
              //   name: 'unlink',
              //   label: 'Desvincular',
              //   icon: 'ri-indeterminate-circle-fill',
              //   classNameColorIcon: 'danger',
              //   onPress: (cellProps) => {
              //     setSelectedProfessionalId(cellProps.id);
              //     toggleUnlinkModal();
              //   },
              // },
            ]}
            canSearch
            searchServer
            onSearch={handleSearchRemote}
            columns={makeColumnsProfessionalsLists()}
            data={professionalsData?.data}
            loading={Boolean(listStatusPage)}
            pagination
            paginationServer
            paginationTotalRows={professionalsData?.total}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            sortServer
            onSort={handleSortRemote}
          />
        </CardBody>
      </Card>

      <ModalConfirmAction
        modalTheme="danger"
        title="Deseja desvincular?"
        isOpen={isUnlinkModalOpen}
        toggle={toggleUnlinkModal}
        onConfirm={() =>
          requestUnlinkProfessional(
            { id: selectedProfessionalId },
            PROFILE_TYPE.TEACHER,
          )
        }
        loading={unlinkModalButtonsStatusPage === STATUSPAGE.LOADING}
      />
    </PageWrapper>
  );
};
